/**
 * Server-side fetcher for OneTrust banner configuration.
 *
 * Calls the OneTrust Server-Side CMP API to retrieve banner UI data,
 * default consent states, and whether the banner should be shown.
 * This data is cached at the Next.js 'use cache' layer (see cached-consent-banner.tsx).
 *
 * Reference: https://developer.onetrust.com/onetrust/docs/get-banner-ui
 */

/** OneTrust configuration — single source of truth for IDs and versions */
export const oneTrustConfig = {
  bannerApi: 'https://mobile-data.onetrust.io/cfw/cmp/v1/banner',
  saveLogConsentApi:
    'https://mobile-data.onetrust.io/cfw/cmp/v1/save-log-consent',
  cdnLocation: 'cdn.cookielaw.org',
  sdkVersion: '202503.1.0',
  deviceType: 'web',
  domainScriptId: '1ba5975d-2070-4418-9c3b-6b9e65f15036',
  /** Apex cookie domain — used for cross-subdomain consent persistence */
  cookieDomain: '.compassion.com',
  policyUrl: 'https://www.compassion.com/privacy-policy/',
} as const;

export interface ConsentBannerButton {
  text: string;
  color: string;
  textColor: string;
  borderRadius?: string;
}

/**
 * Props for the presentational banner UI component.
 * Defined here so both the smart wrapper (in tracking) and the UI component
 * (in ark-skippy) can share a single source of truth without a circular dep.
 */
export interface ConsentBannerUIProps {
  title?: string;
  description?: string;
  acceptAllButton: { text: string; onClick: () => void };
  rejectAllButton?: { text: string; onClick: () => void } | null;
  showPreferencesButton?: {
    text: string;
    onClick: () => void;
    disabled?: boolean;
  } | null;
  policyLink?: { text: string; url: string } | null;
}

export interface ConsentBannerData {
  showBanner: boolean;
  bannerReason?: string;
  layout:
    | 'bottom'
    | 'top'
    | 'center'
    | 'float-bottom-left'
    | 'float-bottom-right';
  backgroundColor: string;
  title: string;
  description: string;
  acceptAllButton: ConsentBannerButton;
  rejectAllButton: ConsentBannerButton | null;
  showPreferencesButton: ConsentBannerButton | null;
  policyLinkText: string;
  policyLinkUrl: string;
  defaultGroupConsents: Record<string, number>;
}

/**
 * Fetch banner configuration from the OneTrust Server-Side CMP API.
 *
 * @param countryCode - ISO 3166-1 Alpha 2 country code (e.g. "US", "CA")
 * @param regionCode - Optional ISO 3166-2 region code (e.g. "CA" for California)
 * @param testMode - If true, appends "-test" to the app ID
 */
export const fetchConsentBannerData = async (
  countryCode?: string,
  regionCode?: string,
  testMode = false,
): Promise<ConsentBannerData | null> => {
  const appId = `${oneTrustConfig.domainScriptId}${testMode ? '-test' : ''}`;

  const headers: Record<string, string> = {
    'OT-CDN-Location': oneTrustConfig.cdnLocation,
    'OT-App-Id': appId,
    'OT-SDK-Version': oneTrustConfig.sdkVersion,
    'OT-Device-Type': oneTrustConfig.deviceType,
    'OT-Consent-String': '',
    'OT-Force-Fetch': 'true',
  };

  if (countryCode) {
    headers['OT-Country-Code'] = countryCode;
  }
  if (regionCode) {
    headers['OT-Region-Code'] = regionCode;
  }

  try {
    const response = await fetch(oneTrustConfig.bannerApi, { headers });

    if (!response.ok) {
      console.error(
        `[consent-banner-data] OneTrust API returned ${response.status}`,
      );
      return null;
    }

    const data = await response.json();

    if (Array.isArray(data.errors) && data.errors.length > 0) {
      console.error('[consent-banner-data] OneTrust API errors:', data.errors);
      return null;
    }

    const { appConfig, bannerUIData, storageKeys } = data;

    if (!bannerUIData) {
      return {
        showBanner: false,
        layout: 'bottom',
        backgroundColor: '#ffffff',
        title: '',
        description: '',
        acceptAllButton: {
          text: 'Accept',
          color: '#1a7b37',
          textColor: '#ffffff',
        },
        rejectAllButton: null,
        showPreferencesButton: null,
        policyLinkText: '',
        policyLinkUrl: '',
        defaultGroupConsents: storageKeys?.OT_GroupConsents ?? {},
      };
    }

    return {
      showBanner: appConfig?.showBanner ?? false,
      bannerReason: appConfig?.bannerReason,
      layout: bannerUIData.general?.layout ?? 'bottom',
      backgroundColor: bannerUIData.general?.backgroundColor ?? '#ffffff',
      title: bannerUIData.summary?.title?.text ?? '',
      description: bannerUIData.summary?.description?.text ?? '',
      acceptAllButton: {
        text: bannerUIData.buttons?.acceptAll?.text ?? 'Accept All',
        color: bannerUIData.buttons?.acceptAll?.color ?? '#1a7b37',
        textColor: bannerUIData.buttons?.acceptAll?.textColor ?? '#ffffff',
        borderRadius: bannerUIData.buttons?.acceptAll?.borderRadius,
      },
      rejectAllButton: bannerUIData.buttons?.rejectAll
        ? {
            text: bannerUIData.buttons.rejectAll.text ?? 'Reject All',
            color: bannerUIData.buttons.rejectAll.color ?? '#1a7b37',
            textColor: bannerUIData.buttons.rejectAll.textColor ?? '#ffffff',
            borderRadius: bannerUIData.buttons.rejectAll.borderRadius,
          }
        : null,
      showPreferencesButton: bannerUIData.buttons?.showPreferences
        ? {
            text:
              bannerUIData.buttons.showPreferences.text ?? 'Cookie Settings',
            color: bannerUIData.buttons.showPreferences.color ?? '#ffffff',
            textColor:
              bannerUIData.buttons.showPreferences.textColor ?? '#1a7b37',
            borderRadius: bannerUIData.buttons.showPreferences.borderRadius,
          }
        : null,
      policyLinkText: bannerUIData.links?.policyLink?.text ?? 'Cookie Policy',
      policyLinkUrl: bannerUIData.links?.policyLink?.url ?? '',
      defaultGroupConsents: storageKeys?.OT_GroupConsents ?? {},
    };
  } catch (error) {
    console.error('[consent-banner-data] Failed to fetch banner data:', error);
    return null;
  }
};

/**
 * Interaction types accepted by the OneTrust save-log-consent endpoint.
 * @see https://developer.onetrust.com/onetrust/docs/save-and-log-consent
 */
export type OneTrustInteractionType =
  | 'BANNER_ALLOW_ALL'
  | 'BANNER_REJECT_ALL'
  | 'BANNER_CLOSE'
  | 'BANNER_CONTINUE_WITHOUT_ACCEPTING'
  | 'PREFERENCE_CENTER_ALLOW_ALL'
  | 'PREFERENCE_CENTER_REJECT_ALL'
  | 'PREFERENCE_CENTER_CONFIRM'
  | 'PREFERENCE_CENTER_CLOSE';

export interface LogConsentInput {
  interactionType: OneTrustInteractionType;
  userAgent: string;
  countryCode?: string;
  regionCode?: string;
  testMode?: boolean;
  /**
   * Per-category consent statuses. Required for `*_CONFIRM` interactions;
   * may be omitted for `*_ALLOW_ALL` / `*_REJECT_ALL` (OneTrust applies
   * defaults based on the interaction type).
   */
  purposesStatus?: { groupId: string; status: boolean }[];
  /**
   * The previous consent string, returned from a prior log-consent call.
   * Persists user identity across sessions on OneTrust's side.
   */
  consentString?: string;
}

export interface LogConsentResult {
  ok: boolean;
  /** Updated consent string to persist for the next log-consent call. */
  consentString?: string;
  errors?: { code: string; message: string }[];
}

/**
 * POST consent decision to OneTrust's save-log-consent endpoint.
 * Returns the updated consent string which should be persisted in a
 * first-party cookie for the next call.
 *
 * Server-only. Call from a Next.js Route Handler — never expose this
 * directly to the browser (the OT-App-Id is config but the endpoint expects
 * server-to-server calls).
 */
export const logConsent = async (
  input: LogConsentInput,
): Promise<LogConsentResult> => {
  const appId = `${oneTrustConfig.domainScriptId}${input.testMode ? '-test' : ''}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'OT-CDN-Location': oneTrustConfig.cdnLocation,
    'OT-App-Id': appId,
    'OT-SDK-Version': oneTrustConfig.sdkVersion,
    'OT-Device-Type': oneTrustConfig.deviceType,
    'OT-Consent-String': input.consentString ?? '',
  };

  if (input.countryCode) {
    headers['OT-Country-Code'] = input.countryCode;
  }
  if (input.regionCode) {
    headers['OT-Region-Code'] = input.regionCode;
  }

  const body: {
    interactionType: OneTrustInteractionType;
    userAgent: string;
    consent?: { purposesStatus: { groupId: string; status: boolean }[] };
  } = {
    interactionType: input.interactionType,
    userAgent: input.userAgent,
  };

  if (input.purposesStatus && input.purposesStatus.length > 0) {
    body.consent = { purposesStatus: input.purposesStatus };
  }

  try {
    const response = await fetch(oneTrustConfig.saveLogConsentApi, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      console.error(
        `[consent-banner-data] OneTrust log-consent returned ${response.status}`,
      );
      return { ok: false };
    }

    const data = await response.json();

    if (Array.isArray(data.errors) && data.errors.length > 0) {
      console.error(
        '[consent-banner-data] OneTrust log-consent errors:',
        data.errors,
      );
      return { ok: false, errors: data.errors };
    }

    return { ok: true, consentString: data.otConsentString };
  } catch (error) {
    console.error('[consent-banner-data] Failed to log consent:', error);
    return { ok: false };
  }
};
